import { apiFetch } from './http';
import type { IdentityDetail } from './auth';

/** An identity of a person, with the avatar the provider knows it by when it is verified. */
export interface ProfileIdentityDetail extends IdentityDetail {
  avatarUrl?: string | null;
}

export interface ProfileView {
  handle: string;
  displayName: string | null;
  /** The Steam avatar of a linked, verified Steam identity; null while there is none. */
  avatarUrl?: string | null;
  joinedAt: string;
  roles: string[];
  identities: string[];
  profileHidden: boolean;
  mine: boolean;
  /** `test`: a smoke night's, an inspection tour's or a guided test's mission; the header does not count it. */
  made: { code: string; title: string; worldName: string; visibility: string; test?: boolean }[];
  coEdited: {
    code: string;
    title: string;
    worldName: string;
    visibility: string;
    test?: boolean;
  }[];
  campaigns: { code: string; title: string; worldName: string; chapter: number; state: string }[];
  directed: { code: string; state: string; outcome: string | null; startedAt: string | null }[];
  ballotsCast: number;
  standings: {
    campaignCode: string | null;
    campaignTitle: string;
    attended: number;
    survived: number;
    medals: string[];
    rank: string;
  }[];
  steamLinked: boolean;
  history: {
    kind: string;
    subject: string | null;
    detail: string | null;
    occurredAt: string;
    /** The same event written for a person to read, already in Catalan; absent on older platforms. */
    text?: string | null;
  }[];
  identityDetails: ProfileIdentityDetail[];
  onboarded: boolean;
  matches: {
    id: number;
    code: string | null;
    serverName: string | null;
    /** The panel's own name of the mission: a file name (`ops_2xqvgqb`) for one the platform published. */
    missionName: string | null;
    /** What a person reads instead: the mission's title, or the terrain when it is gone. Absent from older platforms. */
    displayTitle?: string | null;
    /** A test (smoke night, inspection tour, test card) or a match the panel discarded: not in the header's figure. */
    test?: boolean;
    world: string | null;
    startedAt: string | null;
    durationSeconds: number;
    side: string | null;
    secondsPlayed: number;
    reportUrl: string | null;
  }[];
  /**
   * The header's figures, counted by the platform without its own tests and
   * without discarded matches; `matches` stops at fifty and this does not.
   * Absent from older platforms, where the page counts its lists.
   */
  sessionsPlayed?: number;
  missionsMade?: number;
  /**
   * The platform sessions the person played (on the roster of the linked match,
   * or in the runtime's snapshots), newest first; `directed` are the ones they
   * created. `side` is the roster's, the only role recorded. Absent from older
   * platforms, which sent the directed ones alone.
   */
  played?: {
    code: string;
    title: string;
    state: string;
    startedAt: string | null;
    side: string | null;
    test: boolean;
    reportUrl: string | null;
  }[];
}

export interface ListedProfile {
  handle: string;
  displayName: string | null;
  avatarUrl?: string | null;
  joinedAt: string;
  roles: string[];
}

export const fetchProfiles = () => apiFetch<ListedProfile[]>('/api/profiles');
export const fetchProfile = (handle: string) =>
  apiFetch<ProfileView>(`/api/profiles/${encodeURIComponent(handle)}`);
export const updateProfile = (settings: { displayName?: string; profileHidden?: boolean }) =>
  apiFetch<ProfileView>('/api/profiles/me', { method: 'PUT', body: settings });
/** A typed Steam id or Discord name: a claim, until the provider's button proves it. */
export const claimIdentity = (provider: 'STEAM' | 'DISCORD', subject: string) =>
  apiFetch<ProfileView>('/api/profiles/me/identities', {
    method: 'PUT',
    body: { provider, subject },
  });
export const removeIdentity = (provider: 'STEAM' | 'DISCORD') =>
  apiFetch<ProfileView>(`/api/profiles/me/identities/${provider}`, { method: 'DELETE' });
export const markOnboarded = () => apiFetch<void>('/api/profiles/me/onboarded', { method: 'POST' });
